import { type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { fadeUp, stagger } from '@/components/motion';
import { cn } from '@/lib/cn';

/**
 * Page — top-level layout primitive for every authenticated route. Wraps
 * content in consistent vertical spacing and a motion stagger.
 */
export function Page({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <motion.div
      className={cn('space-y-6', className)}
      initial="hidden"
      animate="show"
      variants={stagger}
    >
      {children}
    </motion.div>
  );
}

/**
 * PageHeader — title + description + actions row.
 *
 *   <PageHeader title="Projects" description="…" actions={<Button>New</Button>} />
 *
 * `eyebrow` adds a small context label above the title (e.g. "Project").
 * `gradient` opt-in renders the title with the marketing gradient.
 *
 * Breadcrumbs are rendered ONCE in the app topbar (see `app-shell.tsx`); do
 * not duplicate them here.
 */
export interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
  gradient?: boolean;
  className?: string;
}

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  gradient = false,
  className,
}: PageHeaderProps): JSX.Element {
  return (
    <motion.header variants={fadeUp} className={cn('space-y-3', className)}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          {eyebrow ? (
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {eyebrow}
            </div>
          ) : null}
          <h1
            className={cn(
              'text-2xl font-semibold leading-tight tracking-tight sm:text-3xl',
              gradient && 'text-gradient',
            )}
          >
            {title}
          </h1>
          {description ? (
            <p className="max-w-2xl text-sm text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        ) : null}
      </div>
    </motion.header>
  );
}

/**
 * PageToolbar — sticky filter/search bar that sits below the header.
 */
export function PageToolbar({
  className,
  ...props
}: HTMLMotionProps<'div'>): JSX.Element {
  return (
    <motion.div
      variants={fadeUp}
      className={cn(
        'sticky top-16 z-10 -mx-1 flex flex-wrap items-center gap-3 rounded-xl border border-border/70 bg-card/70 px-3 py-2 backdrop-blur',
        className,
      )}
      {...props}
    />
  );
}
